'use client';

import type { FC } from 'react';
import React, { useState } from 'react';

import { cn } from '@/lib/utils';

import {
    LocalImage,
    type LocalImageProps,
} from '@/components/atoms/LocalImage';

import PlayButton from './components/PlayButton/PlayButton';

import { env } from '@/env.mjs';

export interface YouTubePlayerProps extends React.HTMLAttributes<HTMLElement> {
    videoId: string;
    image?: LocalImageProps;
    'data-test-id'?: string;
}

const YouTubePlayer: FC<YouTubePlayerProps> = ({
    videoId,
    image,
    ...props
}) => {
    const [isClicked, setIsClicked] = useState<boolean>(false);

    return (
        <div
            className={cn(
                'relative flex w-full max-w-[900px] cursor-pointer justify-center overflow-hidden rounded-3xl border-b-[6px] border-b-typo-primary',
            )}
            onClick={() => setIsClicked(true)}
            {...props}
        >
            <PlayButton className={cn(isClicked && 'hidden')} />

            <div
                className={cn(
                    'pb-[56.25%]',
                    'after:absolute after:inset-0 after:z-10 after:bg-gradient-to-t after:from-grey-900/35 after:to-grey-900/0',
                    isClicked && 'after:hidden',
                )}
            >
                {image && (
                    <LocalImage
                        fill
                        className={cn(
                            'z-10 transition-all',
                            isClicked && 'hidden',
                        )}
                        {...image}
                    />
                )}

                {isClicked && (
                    <iframe
                        height={500}
                        width={900}
                        src={`https://www.youtube.com/embed/${videoId}?enablejsapi=1&autoplay=1&origin=${env.NEXT_PUBLIC_APP_URL}`}
                        title="YouTube video player"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        className={cn('absolute left-0 h-full w-full border-0')}
                    />
                )}
            </div>
        </div>
    );
};

export default YouTubePlayer;
